import React from 'react';
import { motion } from 'framer-motion';

const AboutIEEE: React.FC = () => {
  const stats = [
    { number: '420K+', label: 'Miembros globales' },
    { number: '160+', label: 'Países' },
    { number: '1,900+', label: 'Estándares técnicos' },
  ];

  const features = [
    {
      title: 'Acceso global a publicaciones',
      description: 'Investigaciones técnicas y papers de nivel mundial en IEEE Xplore Digital Library.'
    },
    {
      title: 'Conferencias y eventos',
      description: 'Participa en los eventos de innovación tecnológica más influyentes del mundo.'
    },
    {
      title: 'Estándares que definen',
      description: 'Normas técnicas de referencia que guían la industria global desde telecomunicaciones hasta IA.'
    },
    {
      title: 'Red profesional',
      description: 'Conecta con 420K+ profesionales, investigadores e innovadores en 160+ países.'
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 },
    },
  };

  return (
    <section
      className="relative overflow-hidden bg-slate-50 py-24 sm:py-32 lg:py-40"
      id="about-ieee"
    >
      {/* Ambient background layers - light mode optimized */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] rounded-full blur-[140px] opacity-[0.06] bg-gradient-to-l from-cyan-400/30 to-transparent" />
        <div className="absolute bottom-1/4 left-1/3 w-[500px] h-[500px] rounded-full blur-[130px] opacity-[0.04] bg-gradient-to-t from-indigo-400/20 to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="space-y-16 lg:space-y-20"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="space-y-10 max-w-4xl flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-lg bg-white border border-slate-200 shadow-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-600 shadow-[0_0_8px_rgba(8,145,178,0.4)]" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  IEEE Global
                </span>
              </div>

              <div className="space-y-6 mt-6">
                <h2 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.12] tracking-tight text-slate-900">
                  La comunidad de ingeniería{' '}
                  <span className="text-cyan-600">
                    más grande
                  </span>
                  {' '}del mundo
                </h2>
                <p className="text-lg sm:text-xl text-slate-600 leading-relaxed font-normal max-w-3xl">
                  IEEE impulsa la innovación global conectando profesionales, estudiantes e investigadores en 160+ países. Define los estándares técnicos que guían industrias enteras.
                </p>
              </div>
            </div>
            
            <div className="flex-shrink-0">
              <img
                src="/assets/logos_rama_general_completos/Logo_completo_azul.png"
                alt="IEEE Logo"
                className="h-16 md:h-20 w-auto object-contain transition-opacity duration-300"
              />
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-3 gap-6 sm:gap-10 lg:gap-16 py-10 border-t border-b border-slate-200"
          >
            {stats.map((stat, idx) => (
              <div key={idx} className="flex flex-col gap-3">
                <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-tight">
                  {stat.number}
                </div>
                <div className="text-xs sm:text-sm uppercase tracking-[0.2em] text-slate-500 font-bold">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>

          {/* Features grid */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12"
          >
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="group relative rounded-3xl p-8 lg:p-10 border border-slate-100 bg-white transition-all duration-500 hover:border-cyan-200 hover:shadow-premium"
              >
                <div className="relative space-y-4">
                  <h3 className="text-xl lg:text-2xl font-bold text-slate-900 leading-snug">
                    {feature.title}
                  </h3>
                  <p className="text-base text-slate-500 leading-relaxed font-normal">
                    {feature.description}
                  </p>
                </div>

                <div className="absolute bottom-0 left-8 right-8 h-1 bg-gradient-to-r from-cyan-500/40 to-transparent w-0 group-hover:w-[calc(100%-64px)] transition-all duration-500 rounded-full" />
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Box */}
          <motion.div
            variants={itemVariants}
            className="rounded-[2.5rem] border border-slate-100 bg-white p-10 lg:p-16 text-center space-y-8 shadow-premium"
          >
            <div className="space-y-4">
              <h3 className="text-3xl lg:text-4xl font-black text-slate-900">
                Únete a una comunidad global de innovadores
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto font-normal">
                Como miembro de IEEE ESPOCH, accedes a recursos, eventos y conexiones que transforman carreras técnicas y generan impacto real.
              </p>
            </div>
            <motion.a
              href="#chapters"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-3 px-10 py-4 rounded-xl font-bold text-sm uppercase tracking-[0.2em] bg-cyan-600 text-white transition-all duration-300 shadow-premium hover:bg-cyan-500"
            >
              Explorar capítulos
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutIEEE;
